import { ReactNode, useEffect } from "react"
import { dispatchThunk } from "../thunk/thunkify"
import { useAppDispatch, useAppSelector } from "../store/hooks"
import {
    FetchReportsSpecification,
    GetLinkingRequestsSpecification,
    GetPatientDataBaselineSpecification,
    GetPatientDataCurrentSpecification,
    GetPatientMedicationInformationSpecification,
    GetPatientMedicationRisksSpecification,
    GetPatientOverviewSeveritiesSpecification,
    GetPatientsOfProfessionalSpecification,
    GetProfessionalNotesSpecification,
    PatientDataAvailabilitySpecification,
    ValueType
} from "pokal_api"
import {
    selectDashboardSelectedPatient,
    selectDashboardSelectedValueType,
    selectQuestionnaire
} from "../ProfessionalDashboard"
import notNull from "../../util/notNull"
import { useLocation } from "react-router-dom"
import { selectProfessionalUser } from "../ProfessionalAuthentication"
import { nothing } from "../../util/void"
import { selectPokalApiConfiguredState } from "../PokalApiToaster"
import { persistLocationSelection, persistPatientSelection, persistValueTypeSelection } from "../../storage/selections"

interface PatientDataControllerProps {
    children: ReactNode
}

function PatientDataProvider(props: PatientDataControllerProps) {
    const selectedPatient = useAppSelector(selectDashboardSelectedPatient)
    const selectedValueType = useAppSelector(selectDashboardSelectedValueType)
    const professional = useAppSelector(selectProfessionalUser)
    const pokalApiConfigured = useAppSelector(selectPokalApiConfiguredState)
    const dispatch = useAppDispatch()
    const location = useLocation()

    useEffect(() => {
        persistLocationSelection(location.pathname)
    }, [location])

    function onValueTypeChanged(type: ValueType, patient: string) {
        dispatchThunk(dispatch, GetPatientDataCurrentSpecification, {
            patient: patient,
            valueType: type
        })
        dispatchThunk(dispatch, GetPatientDataBaselineSpecification, {
            patient: patient,
            valueType: type
        })
    }

    useEffect(() => {
        if (pokalApiConfigured && notNull(professional)) {
            dispatchThunk(dispatch, GetLinkingRequestsSpecification, nothing)
            dispatchThunk(
                dispatch,
                GetPatientsOfProfessionalSpecification,
                nothing
            )
        }
    }, [professional, pokalApiConfigured])

    useEffect(() => {
        if (notNull(selectedPatient)) {
            dispatchThunk(dispatch, GetPatientOverviewSeveritiesSpecification, {
                patient: selectedPatient.id
            })
            dispatchThunk(dispatch, PatientDataAvailabilitySpecification, {
                patient: selectedPatient.id
            })
            dispatchThunk(dispatch, GetPatientMedicationRisksSpecification, {
                patient: selectedPatient.id
            })
            dispatchThunk(dispatch, FetchReportsSpecification, {
                patient: selectedPatient.id
            })
            dispatchThunk(
                dispatch,
                GetPatientMedicationInformationSpecification,
                {
                    patient: selectedPatient.id
                }
            )
            dispatchThunk(dispatch, GetProfessionalNotesSpecification, {
                patient: selectedPatient.id
            })
            if (notNull(selectedValueType)) {
                onValueTypeChanged(selectedValueType, selectedPatient.id)
            }
        }
        persistPatientSelection(selectedPatient ? selectedPatient.id : null)
    }, [selectedPatient])

    useEffect(() => {
        if (notNull(selectedValueType) && notNull(selectedPatient)) {
            onValueTypeChanged(selectedValueType, selectedPatient.id)
        }
        dispatch(
            selectQuestionnaire({
                type: null,
                id: null
            })
        )
        persistValueTypeSelection(selectedValueType ? String(selectedValueType) : null)
    }, [selectedValueType])

    return <>{props.children}</>
}

export default PatientDataProvider
